import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

const INACTIVITY_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes

/**
 * Monitors user activity (mouse, keyboard, touch, scroll) and
 * automatically logs out after 15 minutes of inactivity.
 *
 * Mount this once inside the authenticated layout (e.g., DashboardLayout).
 * Every user interaction resets the countdown. When the timer fires,
 * the hook calls logout() and navigates to /login.
 */
export default function useIdleLogout() {
  const { logout } = useAuth();
  const timer = useRef(null);

  const resetTimer = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      logout();
      if (typeof window !== 'undefined') {
        window.location.href = '/login?reason=idle';
      }
    }, INACTIVITY_TIMEOUT_MS);
  }, [logout]);

  useEffect(() => {
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll', 'mousemove'];
    events.forEach((e) => document.addEventListener(e, resetTimer, { passive: true }));
    resetTimer();

    return () => {
      events.forEach((e) => document.removeEventListener(e, resetTimer));
      if (timer.current) clearTimeout(timer.current);
    };
  }, [resetTimer]);
}
